import React, { useState, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { horwathTheme } from '@horwath-htl/shared-branding';
import { ProtectedRoute } from './components/ProtectedRoute';
import { PostLoginRedirect } from './components/PostLoginRedirect';
import { Layout } from './components/Layout';
import { RouteTransitionWrapper } from './components/routing/RouteTransitionWrapper';
import { LoadingSpinner } from './components/LoadingSpinner';
import { DashboardPage } from './pages/DashboardPage';
import HomePage from './pages/HomePage';
import ContinentsPage from './pages/reports-store/ContinentsPage';
import CountriesPage from './pages/reports-store/CountriesPage';
import CountryMarkets from './pages/reports-store/CountryMarkets';
import MarketGroupingDetail from './pages/reports-store/MarketGroupingDetail';
import CustomCompSetsPage from './pages/custom-comp-sets/CustomCompSetsPage';
import SubscriptionsPage from './pages/market-compass/subscriptions';
import TrendReportDetailPage from './pages/trendreport/TrendReportDetailPage';
import MarketPage from './pages/market-compass/market';
import MessagesPage from './pages/messages/MessagesPage';
import MessageThreadPage from './pages/messages/MessageThreadPage';
import { AdminRoute } from './components/AdminRoute';
import { DeveloperRoute } from './components/DeveloperRoute';
import NonActiveMarketGuard from './components/NonActiveMarketGuard';
import { AccessDeniedPage } from './pages/AccessDeniedPage';
import { AuthorizationNeededPage } from './pages/AuthorizationNeededPage';
import { LoginPage } from './pages/LoginPage';
import { PrivacyPage } from './pages/PrivacyPage';
import { EULAPage } from './pages/EULAPage';
import { CartProvider } from './hooks/useCart';
import { IntervalMismatchDialog } from './components/cart/IntervalMismatchDialog';
import { NotificationProvider, useNotification } from './contexts/NotificationContext';
import { OTPAuthProvider } from './auth/providers/OTPAuthProvider';
import { GoogleTag } from './components/GoogleTag';
import { SEODebugger } from './components/SEO/SEODebugger';
import 'flag-icons/css/flag-icons.min.css';

// Lazy load heavy pages for better performance (40-60% faster initial load)
// MarketDetail - Heavy component with charts and data
const MarketDetail = lazy(() => import('./pages/reports-store/MarketDetail'));

// Checkout pages - Only loaded when needed
const CheckoutPage = lazy(() => import('./pages/checkout/CheckoutPage'));
const CheckoutSuccessPage = lazy(() => import('./pages/checkout/CheckoutSuccessPage'));
const CheckoutCancelPage = lazy(() => import('./pages/checkout/CheckoutCancelPage'));
const InvoiceSuccessPage = lazy(() => import('./pages/checkout/InvoiceSuccessPage'));

// Admin pages - Heavy components with data tables and forms
const ReportsAdmin = lazy(() => import('./pages/admin/ReportsAdmin').then(module => ({ default: module.ReportsAdmin })));
const MarketDetailAdmin = lazy(() => import('./pages/admin/MarketDetailAdmin').then(module => ({ default: module.MarketDetailAdmin })));
const ReportDetailAdmin = lazy(() => import('./pages/admin/ReportDetailAdmin').then(module => ({ default: module.ReportDetailAdmin })));
const PodcastDetailAdmin = lazy(() => import('./pages/admin/PodcastDetailAdmin').then(module => ({ default: module.PodcastDetailAdmin })));
const PromptsAdmin = lazy(() => import('./pages/admin/PromptsAdmin').then(module => ({ default: module.PromptsAdmin })));

// API debugging pages - Multiple heavy components
const ApiDebuggingLandingPage = lazy(() => import('./pages/admin/api-debugging').then(module => ({ default: module.ApiDebuggingLandingPage })));
const GeneralApiPage = lazy(() => import('./pages/admin/api-debugging').then(module => ({ default: module.GeneralApiPage })));
const MarketcompassApiPage = lazy(() => import('./pages/admin/api-debugging').then(module => ({ default: module.MarketcompassApiPage })));
const StoreApiPage = lazy(() => import('./pages/admin/api-debugging').then(module => ({ default: module.StoreApiPage })));
const WebhooksApiPage = lazy(() => import('./pages/admin/api-debugging').then(module => ({ default: module.WebhooksApiPage })));
const MarketAnalyticsApiPage = lazy(() => import('./pages/admin/api-debugging').then(module => ({ default: module.MarketAnalyticsApiPage })));

// Performance monitoring - Only for users with developer_tools.view permission
const PerformanceDashboard = lazy(() => import('./pages/PerformanceDashboard'));

// Redirect handler for legacy/products routes
const ProductsRedirect: React.FC = () => {
  const { marketName = '' } = useParams<{ marketName: string }>();
  const decoded = decodeURIComponent(marketName);
  const dashMatches = decoded.match(/-/g) || [];
  const dashCount = dashMatches.length;

  let tokenCandidate: string;
  if (dashCount > 3) {
    const firstDash = decoded.indexOf('-');
    if (firstDash === -1) {
      tokenCandidate = decoded;
    } else {
      const secondDash = decoded.indexOf('-', firstDash + 1);
      tokenCandidate = secondDash === -1 ? decoded : decoded.substring(0, secondDash);
    }
  } else {
    const firstDash = decoded.indexOf('-');
    tokenCandidate = firstDash === -1 ? decoded : decoded.substring(0, firstDash);
  }

  const qValue = tokenCandidate.replace(/-/g, ' ').trim();
  const queryString = qValue ? `?q=${encodeURIComponent(qValue)}` : '';

  return (
    <Navigate
      to={`/global-market-reports/north-america/united-states${queryString}`}
      replace
    />
  );
};

// Wrapper component that manages interval mismatch dialog
const AppWithCart: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [intervalMismatchOpen, setIntervalMismatchOpen] = useState(false);
  const [currentInterval, setCurrentInterval] = useState<'monthly' | 'yearly'>('monthly');
  const [attemptedInterval, setAttemptedInterval] = useState<'monthly' | 'yearly'>('monthly');
  const { showNotification } = useNotification();

  const handleIntervalMismatch = (cartInterval: string, newInterval: string) => {
    setCurrentInterval(cartInterval as 'monthly' | 'yearly');
    setAttemptedInterval(newInterval as 'monthly' | 'yearly');
    setIntervalMismatchOpen(true);
  };

  const handleProductTypeMismatch = (message: string) => {
    // Don't show notification - errors will be shown directly under buttons
    // This callback is kept for backward compatibility
  };

  const handleClearCart = () => {
    // This will be connected to the cart context in the Layout component
    window.dispatchEvent(new window.CustomEvent('clearCartForInterval'));
  };

  const handleKeepCurrent = () => {
    // Just close the dialog
  };

  return (
    <CartProvider
      onIntervalMismatch={handleIntervalMismatch}
      onProductTypeMismatch={handleProductTypeMismatch}
    >
      <IntervalMismatchDialog
        open={intervalMismatchOpen}
        onClose={() => setIntervalMismatchOpen(false)}
        currentInterval={currentInterval}
        attemptedInterval={attemptedInterval}
        onClearCart={handleClearCart}
        onKeepCurrent={handleKeepCurrent}
      />
      {children}
    </CartProvider>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider theme={horwathTheme}>
      <NotificationProvider>
      <OTPAuthProvider>
        <AppWithCart>
          <PostLoginRedirect>
              <GoogleTag />
              <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/access-denied" element={<AccessDeniedPage />} />
            <Route path="/authorization-needed" element={<AuthorizationNeededPage />} />
            <Route path="/checkout/cancel" element={
              <Suspense fallback={<LoadingSpinner message="Loading..." />}>
                <CheckoutCancelPage />
              </Suspense>
            } />
            
            {/* Main layout wrapper */}
            <Route path="/" element={
              <RouteTransitionWrapper>
                <Layout />
              </RouteTransitionWrapper>
            }>
              {/* Public routes that don't require authentication */}
              <Route index element={<HomePage />} />
              <Route path="dashboard" element={<HomePage />} />
              <Route path="dashboard-legacy" element={<DashboardPage />} />
              <Route path="privacy" element={<PrivacyPage />} />
              <Route path="eula" element={<EULAPage />} />
              {/* Browse route merged into /global-market-reports */}
              <Route path="global-market-reports/:continentName/:countryName/:marketName" element={
                <NonActiveMarketGuard>
                  <Suspense fallback={<LoadingSpinner message="Loading market data..." />}>
                    <MarketDetail />
                  </Suspense>
                </NonActiveMarketGuard>
              } />
              <Route path="market-groupings/:id" element={<MarketGroupingDetail />} />
              <Route path="global-market-reports/:continentName/:countryName" element={<CountryMarkets />} />
              <Route path="global-market-reports/:continentName" element={<CountriesPage />} />
              <Route path="global-market-reports" element={<ContinentsPage />} />
              <Route path="custom-comp-sets" element={<CustomCompSetsPage />} />
              {/* Legacy products path redirect */}
              <Route path="products/:marketName" element={<ProductsRedirect />} />
              <Route path="checkout" element={
                <Suspense fallback={<LoadingSpinner message="Loading checkout..." />}>
                  <CheckoutPage />
                </Suspense>
              } />
              
              {/* Protected routes that require authentication */}
              <Route path="subscriptions" element={<ProtectedRoute><SubscriptionsPage /></ProtectedRoute>} />
              <Route path="messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
              <Route path="messages/:threadId" element={<ProtectedRoute><MessageThreadPage /></ProtectedRoute>} />
              <Route path="trendreport/:trendReportId" element={
                <ProtectedRoute>
                  <TrendReportDetailPage />
                </ProtectedRoute>
              } />
              <Route path="market/:marketId" element={<MarketPage />} />
              <Route path="checkout/success" element={
                <ProtectedRoute>
                  <Suspense fallback={<LoadingSpinner message="Processing order..." />}>
                    <CheckoutSuccessPage />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="checkout/invoice-success" element={
                <ProtectedRoute>
                  <Suspense fallback={<LoadingSpinner message="Processing invoice..." />}>
                    <InvoiceSuccessPage />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="admin/api-debugging" element={
                <ProtectedRoute>
                  <Suspense fallback={<LoadingSpinner message="Loading admin panel..." />}>
                    <ApiDebuggingLandingPage />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="admin/api-debugging/general" element={
                <ProtectedRoute>
                  <Suspense fallback={<LoadingSpinner message="Loading API tools..." />}>
                    <GeneralApiPage />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="admin/api-debugging/marketcompass" element={
                <ProtectedRoute>
                  <Suspense fallback={<LoadingSpinner message="Loading API tools..." />}>
                    <MarketcompassApiPage />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="admin/api-debugging/store" element={
                <ProtectedRoute>
                  <Suspense fallback={<LoadingSpinner message="Loading API tools..." />}>
                    <StoreApiPage />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="admin/api-debugging/webhooks" element={
                <ProtectedRoute>
                  <Suspense fallback={<LoadingSpinner message="Loading API tools..." />}>
                    <WebhooksApiPage />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="admin/api-debugging/market-analytics" element={
                <ProtectedRoute>
                  <Suspense fallback={<LoadingSpinner message="Loading API tools..." />}>
                    <MarketAnalyticsApiPage />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="admin/reports" element={
                <ProtectedRoute>
                  <AdminRoute>
                    <Suspense fallback={<LoadingSpinner message="Loading reports admin..." />}>
                      <ReportsAdmin />
                    </Suspense>
                  </AdminRoute>
                </ProtectedRoute>
              } />
              <Route path="admin/prompts" element={
                <ProtectedRoute>
                  <AdminRoute>
                    <Suspense fallback={<LoadingSpinner message="Loading prompts admin..." />}>
                      <PromptsAdmin />
                    </Suspense>
                  </AdminRoute>
                </ProtectedRoute>
              } />
              <Route path="admin/market/:marketId" element={
                <ProtectedRoute>
                  <AdminRoute>
                    <Suspense fallback={<LoadingSpinner message="Loading market admin..." />}>
                      <MarketDetailAdmin />
                    </Suspense>
                  </AdminRoute>
                </ProtectedRoute>
              } />
              <Route path="admin/market/:marketId/report/:reportId" element={
                <ProtectedRoute>
                  <AdminRoute>
                    <Suspense fallback={<LoadingSpinner message="Loading report admin..." />}>
                      <ReportDetailAdmin />
                    </Suspense>
                  </AdminRoute>
                </ProtectedRoute>
              } />
              <Route path="admin/market/:marketId/podcast/:podcastId" element={
                <ProtectedRoute>
                  <AdminRoute>
                    <Suspense fallback={<LoadingSpinner message="Loading podcast admin..." />}>
                      <PodcastDetailAdmin />
                    </Suspense>
                  </AdminRoute>
                </ProtectedRoute>
              } />
              <Route path="performance" element={
                <ProtectedRoute>
                  <DeveloperRoute>
                    <Suspense fallback={<LoadingSpinner message="Loading performance dashboard..." />}>
                      <PerformanceDashboard />
                    </Suspense>
                  </DeveloperRoute>
                </ProtectedRoute>
              } />
            </Route>
            </Routes>
          </PostLoginRedirect>
        </AppWithCart>
        </OTPAuthProvider>
      </NotificationProvider>

      {/* SEO Debugger - Available on all pages (controlled by window.ENV.SEO_DEBUGGER_ENABLED) */}
      <SEODebugger />
    </ThemeProvider>
  );
};

export default App;